import { useMemo } from 'react';

// Centralised read of the logged-in session.
//
// The session lives in localStorage (written by redux/authSlice.js at login,
// wiped by localStorage.clear() at logout). Historically every component read
// those keys directly — `localStorage.getItem('Role')` appears in ~50 files.
// That was tolerable for one key; adding tenantId the same way would spread a
// second, subtler one just as far. New code should read the session through
// here instead, so the storage shape stays changeable in one place.
//
// Existing getItem('Role') call sites are deliberately left alone — migrating
// them is a separate cleanup, not part of the multi-tenant work.
//
// SECURITY: none of this is trusted by the backend. The API derives role and
// tenant from the signed JWT, so editing these values in devtools changes what
// the UI draws, never what the server returns.

/** Non-hook variant, for use outside React components. */
export const readSession = () => {
  const rawRole = localStorage.getItem('Role') || null;
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  // Empty string means "no tenant" (super_admin / patient) — normalise to null
  // so callers only ever check for a falsy value.
  const tenantId = localStorage.getItem('TenantId') || null;
  // The tenant's country. Staff-creation forms display this instead of asking,
  // because doctors and admins inherit their country from their tenant.
  const tenantCountryId = localStorage.getItem('TenantCountryId') || null;

  return {
    // 'doctor' | 'patient' | 'admin'. super_admin is stored as 'admin' so every
    // existing role === 'admin' check keeps working; isSuperAdmin is the only
    // client-side distinction (see authSlice.js).
    role: rawRole,
    isSuperAdmin,
    isAdmin: rawRole === 'admin',
    isDoctor: rawRole === 'doctor',
    isPatient: rawRole === 'patient',
    // Tenant STAFF — an employee of this hospital. What they may DO comes from
    // their sub-role, not from here: read that with hooks/usePermissions.js.
    isStaff: rawRole === 'user',
    staffRoleName: localStorage.getItem('StaffRoleName') || null,
    userId: localStorage.getItem('ID') || null,
    isAuthenticated: localStorage.getItem('isAuthenticated') === 'true',
    // The tenant this session acts inside. null for super_admin (they manage
    // every tenant) and for patients (one global identity across tenants).
    tenantId,
    tenantCountryId,
    // True when the session is confined to a single tenant — i.e. a doctor or
    // a plain admin. Useful for hiding cross-tenant UI.
    isTenantBound: Boolean(tenantId) && !isSuperAdmin,
  };
};

/**
 * Read the current session inside a component.
 *
 * Note this is a snapshot, not a subscription: localStorage doesn't notify on
 * change, and the session only changes at login/logout, both of which already
 * navigate. Don't expect it to re-render on its own.
 */
const useSession = () => useMemo(() => readSession(), []);

export default useSession;
